"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import Text from "@/components/Text";
import DualRangeSlider from "@/components/DualRangeSlider";
import useElementHeight from "@/hooks/useElementHeight";
import useFocusTrap from "@/hooks/useFocusTrap";
import classNames from "@/helpers/classNames";
import type { FilterOptions } from "@/tools/medusa";
import styles from "./styles.module.scss";

export interface ActiveFilters {
  sort: string;
  categoryIds: string[];
  typeIds: string[];
  priceMin: number;
  priceMax: number;
}

interface CollectionFilterPanelProps {
  show: boolean;
  onClose: () => void;
  onApply: (filters: ActiveFilters) => void;
  filterOptions: FilterOptions;
  initialFilters: ActiveFilters;
}

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection = ({ title, defaultOpen = false, children }: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [ref, height] = useElementHeight({ disabled: !isOpen });

  return (
    <div className={styles.filterSection}>
      <button
        type="button"
        className={styles.filterSectionHeader}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <Text text={title} size="sm" weight="medium" />
        <Icon
          title="plusBold"
          size="md"
          className={classNames(styles.toggleIcon, {
            [styles.toggleIconOpen]: isOpen,
          })}
        />
      </button>
      {/* Collapsed to no height, its options stayed in the tab order, as
          AccordionItem's did before it took the same fix. */}
      <div
        className={styles.filterSectionContent}
        style={{ maxHeight: isOpen && height ? height : 0 }}
        inert={!isOpen}
      >
        <div ref={ref} className={styles.filterSectionInner}>
          {children}
        </div>
      </div>
    </div>
  );
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "title_asc", label: "A-Z" },
];

const formatPrice = (value: number) => value.toFixed(2);

const CollectionFilterPanel = (props: CollectionFilterPanelProps) => {
  const { show, onClose, onApply, filterOptions, initialFilters } = props;

  const [sort, setSort] = useState(initialFilters.sort);
  const [categoryIds, setCategoryIds] = useState<string[]>(initialFilters.categoryIds);
  const [typeIds, setTypeIds] = useState<string[]>(initialFilters.typeIds);
  const [priceMin, setPriceMin] = useState(initialFilters.priceMin);
  const [priceMax, setPriceMax] = useState(initialFilters.priceMax);

  // Buffered string values for the price text inputs so users can type freely
  // without each keystroke being reformatted to "X.00" mid-typing. The buffers
  // commit to priceMin/priceMax onBlur (and on apply via handleApply).
  const [priceMinInput, setPriceMinInput] = useState(formatPrice(initialFilters.priceMin));
  const [priceMaxInput, setPriceMaxInput] = useState(formatPrice(initialFilters.priceMax));

  // Resync local form state with the parent's filters whenever the panel
  // opens, so changes made elsewhere (e.g. the sort dropdown in
  // CollectionFilterBar) are reflected when the panel is reopened.
  useEffect(() => {
    if (!show) {
      return;
    }
    setSort(initialFilters.sort);
    setCategoryIds(initialFilters.categoryIds);
    setTypeIds(initialFilters.typeIds);
    setPriceMin(initialFilters.priceMin);
    setPriceMax(initialFilters.priceMax);
  }, [show, initialFilters]);

  // Sync the input buffers when the slider (or external state) updates the
  // committed price values. The buffer pattern lets the slider stay live
  // while the inputs only reformat when not being typed in.
  useEffect(() => {
    setPriceMinInput(formatPrice(priceMin));
  }, [priceMin]);
  useEffect(() => {
    setPriceMaxInput(formatPrice(priceMax));
  }, [priceMax]);

  const commitPriceMin = (raw: string) => {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      setPriceMin(parsed);
    } else {
      setPriceMinInput(formatPrice(priceMin));
    }
  };

  const commitPriceMax = (raw: string) => {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      setPriceMax(parsed);
    } else {
      setPriceMaxInput(formatPrice(priceMax));
    }
  };

  const toggleCategory = useCallback((id: string) => {
    setCategoryIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }, []);

  const toggleType = useCallback((id: string) => {
    setTypeIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  }, []);

  // Sort is excluded from the active filter count — selecting a sort order
  // is a view preference, not a filter being applied.
  const activeCount =
    categoryIds.length +
    typeIds.length +
    (priceMin !== filterOptions.priceMin ? 1 : 0) +
    (priceMax !== filterOptions.priceMax ? 1 : 0);

  const handleApply = () => {
    // Parse the input buffers as source of truth so a click on Apply that
    // happens before the input blurs (e.g. tap-to-apply on mobile) still
    // commits the typed value rather than the last blurred one.
    const parsedMin = Number(priceMinInput);
    const parsedMax = Number(priceMaxInput);
    onApply({
      sort,
      categoryIds,
      typeIds,
      priceMin: Number.isFinite(parsedMin) ? parsedMin : priceMin,
      priceMax: Number.isFinite(parsedMax) ? parsedMax : priceMax,
    });
    onClose();
  };

  useEffect(() => {
    if (show) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [show]);

  const portalRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    portalRef.current = document.querySelector("#modal");
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && show) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, onClose]);

  useFocusTrap(panelRef, show);

  if (!mounted || !portalRef.current) {
    return null;
  }

  return createPortal(
    // Portalled to `#modal`, which sits outside any themed subtree, so theme
    // custom properties resolve to nothing here. That silently broke every
    // `:focus-visible` rule below: `outline: 2px solid var(--fg-brand)` with an
    // undefined `--fg-brand` is invalid, so the whole shorthand fell back to
    // `outline-style: none` and no focus ring was drawn at all. The panel is
    // white-backgrounded, matching this theme.
    <div data-theme="white" className={classNames(styles.drawer, { [styles.open]: show })}>
      <div className={styles.overlay} onClick={onClose} />
      <aside
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Filter products"
      >
        <div className={styles.header}>
          <Text text="Filter" variant="heading" size="sm" />
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close filters"
          >
            <Icon title="close" size="sm" color="currentColor" />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.priceSection}>
            <Text text="Price range" size="md" weight="medium" />
            <div className={styles.priceFilters}>
              <div className={styles.priceLabels}>
                <Text text="Minimum" size="sm" variant="label" />
                <Text text="Maximum" size="sm" variant="label" />
              </div>
              <DualRangeSlider
                min={filterOptions.priceMin}
                max={filterOptions.priceMax}
                valueMin={priceMin}
                valueMax={priceMax}
                step={1}
                onChange={(min, max) => {
                  setPriceMin(min);
                  setPriceMax(max);
                }}
              />

              <div className={styles.priceInputs}>
                <div className={styles.priceField}>
                  <Icon className={styles.dollarIcon} title="currencyDollar" size="sm" />
                  <input
                    type="number"
                    value={priceMinInput}
                    onChange={(e) => setPriceMinInput(e.target.value)}
                    onBlur={(e) => commitPriceMin(e.target.value)}
                    min={filterOptions.priceMin}
                    max={priceMax}
                    step="1"
                    aria-label="Minimum price"
                  />
                </div>
                <Text text="to" variant="label" size="md" className={styles.priceSeparator} />
                <div className={styles.priceField}>
                  <Icon className={styles.dollarIcon} title="currencyDollar" size="sm" />
                  <input
                    type="number"
                    value={priceMaxInput}
                    onChange={(e) => setPriceMaxInput(e.target.value)}
                    onBlur={(e) => commitPriceMax(e.target.value)}
                    min={priceMin}
                    max={filterOptions.priceMax}
                    step="1"
                    aria-label="Maximum price"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          <CollapsibleSection title="Sort by">
            <div className={styles.radioList}>
              {SORT_OPTIONS.map((option) => (
                <label key={option.value} className={styles.radioItem}>
                  <input
                    type="radio"
                    name="sort"
                    value={option.value}
                    checked={sort === option.value}
                    onChange={() => setSort(option.value)}
                  />
                  <span className={styles.checkboxControl} />
                  <Text text={option.label} size="lg" variant="label" />
                </label>
              ))}
            </div>
          </CollapsibleSection>

          <div className={styles.divider} />

          {filterOptions.categories.length > 0 && (
            <>
              <CollapsibleSection title="Filter by Occasion" defaultOpen>
                <div className={styles.checkboxList}>
                  {filterOptions.categories.map((category) => (
                    <label key={category.id} className={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={categoryIds.includes(category.id)}
                        onChange={() => toggleCategory(category.id)}
                      />
                      <span className={styles.checkboxControl} />
                      <Text text={category.value} size="md" />
                    </label>
                  ))}
                </div>
              </CollapsibleSection>
              <div className={styles.divider} />
            </>
          )}

          {filterOptions.types.length > 0 && (
            <>
              <CollapsibleSection title="Product Type">
                <div className={styles.checkboxList}>
                  {filterOptions.types.map((type) => (
                    <label key={type.id} className={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={typeIds.includes(type.id)}
                        onChange={() => toggleType(type.id)}
                      />
                      <span className={styles.checkboxControl} />
                      <Text text={type.value} size="md" />
                    </label>
                  ))}
                </div>
              </CollapsibleSection>
              <div className={styles.divider} />
            </>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            text={`Apply filters${activeCount > 0 ? ` (${activeCount})` : ""}`}
            theme="yellow"
            size="lg"
            className={styles.applyButton}
            onClick={handleApply}
            ariaLabel="Apply filters"
          />
        </div>
      </aside>
    </div>,
    portalRef.current,
  );
};

export default CollectionFilterPanel;
export type { CollectionFilterPanelProps };
